import { Alert, Button, Card, InputNumber, Modal, Select, Skeleton, Space, Spin, Steps, Table, Tag, Typography, message } from "antd";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";
import { CheckCircleOutlined, ClockCircleOutlined, SafetyCertificateOutlined, WalletOutlined } from "@ant-design/icons";
import { createWalletTransfer, getCommercialOnboardingUrl, getFundBalanceByUid, getFundTransactionsByUid, getLinkAccountUrl, getLinkedAccounts, getOnboardingStatus } from "../../actions/Actions";
import { formatAmount, formatdate } from "../../util";
import "./Payments.css";

const { Title, Paragraph, Text } = Typography;

const PENDING_STATUSES = ["STARTED", "Pending", "PendingCip", "PendingMemberQualification", "PendingFunding"];
const ONBOARDING_LOADING_MESSAGES = [
  "Checking account onboarding status...",
  "Fetching wallet provisioning information...",
  "Verifying business profile progress...",
  "Syncing latest status from payments service...",
];

function toBool(value) {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const norm = value.trim().toLowerCase();
    return ["true", "1", "yes", "y"].includes(norm);
  }
  return false;
}

function isFundCreated(status) {
  if (!status || typeof status !== "object") return false;

  const explicitFields = [
    status.fund_created,
    status.wallet_created,
    status.has_wallet,
    status.is_fund_created,
    status.is_wallet_created,
    status.fund_exists,
  ];
  if (explicitFields.some((v) => toBool(v))) return true;

  const uidFields = [
    status.fund_uid,
    status.wallet_fund_uid,
    status.primary_fund_uid,
    status.external_payments_id,
    status.bolt_account_fund_uid,
  ];
  if (uidFields.some((v) => typeof v === "string" && v.trim().length > 0)) return true;

  if (status.fund && typeof status.fund === "object") {
    if (typeof status.fund.uid === "string" && status.fund.uid.trim().length > 0) return true;
    if (toBool(status.fund.created)) return true;
  }

  return false;
}

function Payments() {
  const { isAuthenticated, user, getAccessTokenSilently } = useAuth0();
  const dispatch = useDispatch();
  const currentDealerhost = useSelector((state) => state.dealerHosts.current);

  const [walletOnboardingLoading, setWalletOnboardingLoading] = useState(false);
  const [onboardingStatusLoading, setOnboardingStatusLoading] = useState(false);
  const [onboardingStatus, setOnboardingStatus] = useState({ effective_status: "NOT_STARTED", actual_status: null });
  const [fundBalance, setFundBalance] = useState(null);
  const [fundTransactions, setFundTransactions] = useState([]);
  const [walletDataLoading, setWalletDataLoading] = useState(false);
  const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);
  const [linkRequiredModalOpen, setLinkRequiredModalOpen] = useState(false);
  const [withdrawSubmitting, setWithdrawSubmitting] = useState(false);
  const [linkAccountSubmitting, setLinkAccountSubmitting] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState(null);
  const [withdrawAmountError, setWithdrawAmountError] = useState(null);
  const [selectedLinkedAccountUid, setSelectedLinkedAccountUid] = useState(null);
  const [hasLinkedAccount, setHasLinkedAccount] = useState(false);
  const [linkedAccount, setLinkedAccount] = useState(null);
  const [linkedAccounts, setLinkedAccounts] = useState([]);
  const [linkedAccountsLoading, setLinkedAccountsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState("wallet");
  const [initialStatusResolved, setInitialStatusResolved] = useState(false);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const pollRef = useRef(null);

  const loadOnboardingStatus = useCallback(() => {
    if (!isAuthenticated || !currentDealerhost) {
      setInitialStatusResolved(true);
      return Promise.resolve();
    }

    setOnboardingStatusLoading(true);
    return getAccessTokenSilently()
      .then((token) => dispatch(getOnboardingStatus(token, currentDealerhost?.dealer_id)))
      .then((data) => {
        if (data && typeof data === "object") {
          setOnboardingStatus(data);
        }
      })
      .catch(() => {
        message.error("Unable to refresh onboarding status. Please try again.");
      })
      .finally(() => {
        setOnboardingStatusLoading(false);
        setInitialStatusResolved(true);
      });
  }, [currentDealerhost, dispatch, getAccessTokenSilently, isAuthenticated]);

  const loadFundData = useCallback((fundUid) => {
    if (!fundUid || !isAuthenticated) {
      return Promise.resolve();
    }

    setWalletDataLoading(true);
    return getAccessTokenSilently()
      .then((token) => Promise.all([
        dispatch(getFundBalanceByUid(token, fundUid)),
        dispatch(getFundTransactionsByUid(token, fundUid)),
      ]))
      .then(([balanceData, txData]) => {
        setFundBalance(balanceData || null);
        setFundTransactions(Array.isArray(txData?.items) ? txData.items : []);
      })
      .catch(() => {
        message.error("Unable to load wallet data. Please try again.");
      })
      .finally(() => {
        setWalletDataLoading(false);
      });
  }, [dispatch, getAccessTokenSilently, isAuthenticated]);



  const loadLinkedAccounts = useCallback(() => {
    if (!isAuthenticated || !currentDealerhost?.dealer_id) {
      setLinkedAccounts([]);
      setHasLinkedAccount(false);
      setLinkedAccount(null);
      return Promise.resolve();
    }

    setLinkedAccountsLoading(true);
    return getAccessTokenSilently()
      .then((token) => dispatch(getLinkedAccounts(token, currentDealerhost.dealer_id)))
      .then((items) => {
        const normalizedItems = Array.isArray(items) ? items : [];
        // Backend already filters for LinkedAccount fundType; keep list as-is.
        setLinkedAccounts(normalizedItems);
        setHasLinkedAccount(normalizedItems.length > 0);
        const fallbackAccount = normalizedItems.length > 0 ? normalizedItems[0] : null;
        setLinkedAccount(fallbackAccount);
        setSelectedLinkedAccountUid((prev) => prev || (fallbackAccount ? fallbackAccount.uid : null));
      })
      .catch((error) => {
        console.error("Unable to load linked accounts", error);
        setLinkedAccounts([]);
        setHasLinkedAccount(false);
        setLinkedAccount(null);
      })
      .finally(() => {
        setLinkedAccountsLoading(false);
      });
  }, [currentDealerhost, dispatch, getAccessTokenSilently, isAuthenticated]);

  const handleWithdraw = () => {
    const available = Number(fundBalance?.balanceAvailable || 0);
    const amountNum = Number(withdrawAmount || 0);
    const selectedLinkedAccount = linkedAccounts.find((item) => item && item.uid === selectedLinkedAccountUid) || null;

    if (!hasLinkedAccount || linkedAccounts.length === 0) {
      message.error("Please link an external account before withdrawing funds.");
      return;
    }
    if (!selectedLinkedAccountUid || !selectedLinkedAccount) {
      message.error("Please select an external account.");
      return;
    }
    if (!amountNum || amountNum <= 0) {
      setWithdrawAmountError("Please enter a valid amount.");
      return;
    }
    if (amountNum > available) {
      setWithdrawAmountError("Amount exceeds available balance.");
      return;
    }
    if (!currentDealerhost?.dealer_id || !isAuthenticated || !fundUid) {
      console.error("withdraw prerequisites missing", {
        dealerId: currentDealerhost?.dealer_id,
        isAuthenticated,
        fundUid,
      });
      message.error("Unable to process withdrawal right now. Please try again.");
      return;
    }

    setWithdrawSubmitting(true);
    getAccessTokenSilently()
      .then((token) => dispatch(createWalletTransfer(token, {
        type: "DEALER",
        type_id: currentDealerhost.dealer_id,
        sourceFundUid: fundUid,
        destinationFundUid: selectedLinkedAccountUid,
        amount: amountNum,
        sourceName: currentDealerhost?.dealer_nm || `DEALER-${currentDealerhost.dealer_id}`,
        destinationName: selectedLinkedAccount?.financialInstitutionName || "Linked Account",
        requestedBy: user?.email || "system",
      })))
      .then(() => {
        setWithdrawModalOpen(false);
        setWithdrawAmount(null);
        setWithdrawAmountError(null);
        return Promise.all([
          fundUid ? loadFundData(fundUid) : Promise.resolve(),
          loadLinkedAccounts(),
        ]);
      })
      .catch((error) => {
        console.error("withdraw modal submit failed", error);
        message.error("Unable to process withdrawal right now. Please try again.");
      })
      .finally(() => {
        setWithdrawSubmitting(false);
      });
  };

  const handleWithdrawClick = () => {
    if (!hasLinkedAccount) {
      setLinkRequiredModalOpen(true);
      return;
    }
    setWithdrawAmountError(null);
    setWithdrawModalOpen(true);
  };

  const handleContinueToLinkAccount = () => {
    if (!currentDealerhost?.dealer_id || !isAuthenticated) {
      message.error("Unable to start account linking right now.");
      return;
    }

    setLinkAccountSubmitting(true);
    getAccessTokenSilently()
      .then((token) => dispatch(getLinkAccountUrl(token, currentDealerhost.dealer_id)))
      .then((data) => {
        const linkAccountUrl = data && data.linkAccountUrl ? String(data.linkAccountUrl).trim() : "";
        if (!linkAccountUrl || linkAccountUrl.indexOf("token=") === -1) {
          throw new Error("Invalid link-account URL");
        }

        // External account linking requires redirect to Bolt-hosted flow.
        window.location.assign(linkAccountUrl);
      })
      .catch((error) => {
        console.error("Link account flow failed", error);
        message.error("Unable to start account linking. Please try again.");
      })
      .finally(() => {
        setLinkAccountSubmitting(false);
      });
  };
  const openWalletFlow = () => {
    if (!isAuthenticated || !currentDealerhost) {
      return;
    }

    setWalletOnboardingLoading(true);
    getAccessTokenSilently()
      .then((token) => dispatch(getCommercialOnboardingUrl(token, currentDealerhost?.dealer_id)))
      .then((data) => {
        const onboardingUrl = data && data.onboardingUrl ? String(data.onboardingUrl).trim() : "";
        if (!onboardingUrl || onboardingUrl.indexOf("token=") === -1) {
          throw new Error("Invalid onboarding URL");
        }
        window.location.assign(onboardingUrl);
      })
      .catch(() => {
        message.error("Unable to launch onboarding. Please try again.");
      })
      .finally(() => {
        setWalletOnboardingLoading(false);
      });
  };

  useEffect(() => {
    loadOnboardingStatus();
  }, [loadOnboardingStatus]);
  useEffect(() => {
    if (!onboardingStatusLoading || initialStatusResolved) {
      return undefined;
    }
    const timer = setInterval(() => {
      setLoadingMessageIndex((prev) => (prev + 1) % ONBOARDING_LOADING_MESSAGES.length);
    }, 1400);
    return () => clearInterval(timer);
  }, [initialStatusResolved, onboardingStatusLoading]);

  const effectiveStatus = onboardingStatus?.effective_status || "NOT_STARTED";
  const actualStatus = onboardingStatus?.actual_status || null;
  const fundCreated = useMemo(() => isFundCreated(onboardingStatus), [onboardingStatus]);
  const fundUid = onboardingStatus?.fund_uid || onboardingStatus?.wallet_fund_uid || onboardingStatus?.primary_fund_uid || null;
  const verified = effectiveStatus === "Active" && fundCreated;

  const uiState = useMemo(() => {
    if (verified) {
      return "verified_ready";
    }
    if (effectiveStatus === "NOT_STARTED" || !effectiveStatus) {
      return "not_setup";
    }
    if (PENDING_STATUSES.includes(effectiveStatus) || (effectiveStatus === "Active" && !fundCreated)) {
      return "pending_verification";
    }
    return "pending_verification";
  }, [effectiveStatus, fundCreated, verified]);

  useEffect(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }

    if (uiState === "pending_verification") {
      pollRef.current = setInterval(() => {
        loadOnboardingStatus();
      }, 30000);
    }

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [uiState, loadOnboardingStatus]);

  useEffect(() => {
    if (uiState === "verified_ready" && fundUid) {
      loadFundData(fundUid);
    }
  }, [fundUid, loadFundData, uiState]);

  useEffect(() => {
    if (uiState === "verified_ready") {
      loadLinkedAccounts();
    }
  }, [loadLinkedAccounts, uiState]);

  useEffect(() => {
    const runtimeLinkedAccount =
      onboardingStatus?.linked_account ||
      onboardingStatus?.external_account ||
      onboardingStatus?.bank_account ||
      null;
    const runtimeHasLinkedAccount = runtimeLinkedAccount && typeof runtimeLinkedAccount === "object";

    // Keep legacy onboarding payload as fallback only if linked-accounts API has not populated yet.
    if (runtimeHasLinkedAccount && linkedAccounts.length === 0) {
      setHasLinkedAccount(true);
      setLinkedAccount((prev) => prev || runtimeLinkedAccount);
    }
  }, [linkedAccounts.length, onboardingStatus]);


  if (!initialStatusResolved) {
    return (
      <div className="payments-shell">
        <Card className="payments-card" bordered={false}>
          <Space direction="vertical" size={16} style={{ width: "100%", textAlign: "center" }}>
            <Spin size="large" />
            <Title level={3} className="payments-title">Preparing your payments workspace</Title>
            <Paragraph className="payments-subtitle">{ONBOARDING_LOADING_MESSAGES[loadingMessageIndex]}</Paragraph>
            <Skeleton active paragraph={{ rows: 2 }} title={false} />
          </Space>
        </Card>
      </div>
    );
  }
  const statusTag = (
    <Tag color={verified ? "success" : "processing"} className="payments-status-tag">
      Status: {effectiveStatus}{actualStatus && actualStatus !== effectiveStatus ? ` (${actualStatus})` : ""}
    </Tag>
  );

  if (uiState === "not_setup") {
    return (
      <div className="payments-shell">
        <Card className="payments-card" bordered={false}>
          <Space direction="vertical" size={16} style={{ width: "100%" }}>
            <Text className="payments-eyebrow">Wallet Onboarding</Text>
            <Title level={2} className="payments-title">Open your dealer wallet</Title>
            <Paragraph className="payments-subtitle">
              Complete KYB to enable secure wallet payouts and transaction processing in FastPass.
            </Paragraph>

            <Space direction="vertical" size={10} className="payments-benefits">
              <Text><CheckCircleOutlined /> Business verification workflow</Text>
              <Text><SafetyCertificateOutlined /> Compliance-first KYB/KYC checks</Text>
              <Text><WalletOutlined /> Wallet activation for instant payout readiness</Text>
            </Space>

            <Button
              type="primary"
              size="large"
              className="payments-primary-btn"
              onClick={openWalletFlow}
              loading={walletOnboardingLoading}
            >
              Start KYB
            </Button>
          </Space>
        </Card>
      </div>
    );
  }

  if (uiState === "pending_verification") {
    const pendingCip = effectiveStatus === "PendingCip";

    return (
      <div className="payments-shell">
        <Card className="payments-card" bordered={false}>
          <Space direction="vertical" size={16} style={{ width: "100%" }}>
            <Text className="payments-eyebrow">Wallet Onboarding</Text>
            {statusTag}
            <Title level={2} className="payments-title">
              {pendingCip ? "Identity verification in progress" : "Verification in progress"}
            </Title>
            <Paragraph className="payments-subtitle">
              {pendingCip
                ? "Your business information is under CIP review. We will activate the wallet once compliance checks are complete."
                : "Your KYB submission is being reviewed. This page auto-refreshes every 30 seconds."}
            </Paragraph>

            <Alert
              type="info"
              showIcon
              icon={<ClockCircleOutlined />}
              message="Automatic status refresh is enabled every 30 seconds."
            />

            <Steps
              size="small"
              current={effectiveStatus === "Active" && !fundCreated ? 2 : 1}
              items={[
                { title: "KYB Started" },
                { title: "Verification" },
                { title: "Wallet Provisioning" },
              ]}
            />

            <Space>
              <Button onClick={loadOnboardingStatus} loading={onboardingStatusLoading}>Refresh Status</Button>
              {effectiveStatus === "STARTED" ? (
                <Button type="primary" className="payments-primary-btn" onClick={openWalletFlow} loading={walletOnboardingLoading}>
                  Continue KYB
                </Button>
              ) : null}
            </Space>
          </Space>
        </Card>
      </div>
    );
  }

  const txColumns = [
    { title: "Transfer UID", dataIndex: "uid", key: "uid", render: (v) => <span style={{ whiteSpace: "normal", wordBreak: "break-all" }}>{v}</span> },
    { title: "Source Fund UID", dataIndex: ["source", "fundUid"], key: "sourceFundUid", render: (v) => <span style={{ whiteSpace: "normal", wordBreak: "break-all" }}>{v}</span> },
    { title: "Destination Fund UID", dataIndex: ["destination", "fundUid"], key: "destinationFundUid", render: (v) => <span style={{ whiteSpace: "normal", wordBreak: "break-all" }}>{v}</span> },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (v) => formatAmount(v),
    },
    {
      title: "Date", dataIndex: "created", key: "createdDate", render: (v) => <span style={{ whiteSpace: "nowrap" }}>{formatdate(v)}</span>,
    },
    {
      title: "Time", dataIndex: "created", key: "createdTime", render: (v) => { if (!v) return "-"; const d = new Date(v); if (Number.isNaN(d.getTime())) return "-"; return <span style={{ whiteSpace: "nowrap" }}>{d.toLocaleTimeString()}</span>; },
    },
    { title: "Method", dataIndex: "transferMethod", key: "transferMethod" },
    { title: "Status", dataIndex: "transferStatus", key: "transferStatus" },
  ];

  return (
    <div className="payments-verified-layout">
      <div className="payments-topbar">
        <div className="payments-brand">
          <WalletOutlined />
          <span>PayPortal</span>
        </div>
        <div className="payments-tabs">
          <Button
            type={currentPage === "wallet" ? "primary" : "default"}
            className={currentPage === "wallet" ? "payments-primary-btn" : ""}
            onClick={() => setCurrentPage("wallet")}
          >
            Wallet
          </Button>
          <Button
            type={currentPage === "transactions" ? "primary" : "default"}
            className={currentPage === "transactions" ? "payments-primary-btn" : ""}
            onClick={() => setCurrentPage("transactions")}
          >
            Transactions
          </Button>
        </div>
      </div>

      <div className="payments-content-wrap">
        {currentPage === "wallet" ? (
          <Card className="payments-balance-card" bordered>
            <Space direction="vertical" style={{ width: "100%" }} size={12}>
              {statusTag}
              <Text type="secondary">Available Balance</Text>
              {walletDataLoading ? (
                <Skeleton active paragraph={{ rows: 1 }} title={false} />
              ) : (
                <Title level={1} className="payments-balance-value">
                  ${Number(fundBalance?.balanceAvailable || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Title>
              )}
              <Button
                type="primary"
                className="payments-primary-btn"
                onClick={handleWithdrawClick}
                disabled={walletDataLoading || Number(fundBalance?.balanceAvailable || 0) <= 0}
              >
                Withdraw Funds
              </Button>
            </Space>

            <Card bordered style={{ marginTop: 16 }}>
              <Space direction="vertical" size={8} style={{ width: "100%" }}>
                <Space style={{ justifyContent: "space-between", width: "100%" }}>
                  <Title level={5} style={{ margin: 0 }}>External Account Linking</Title>
                  <Tag color={hasLinkedAccount ? "success" : "warning"}>
                    {hasLinkedAccount ? "Linked" : "Required"}
                  </Tag>
                </Space>

                {linkedAccountsLoading ? (
                  <Skeleton active paragraph={{ rows: 2 }} title={false} />
                ) : !hasLinkedAccount ? (
                  <>
                    <Text>No external account linked</Text>
                    <Text type="secondary">Connect your bank account to enable wallet-to-bank transfers.</Text>
                    <div>
                      <Button onClick={() => setLinkRequiredModalOpen(true)}>Link External Account</Button>
                    </div>
                  </>
                ) : (
                  <Space direction="vertical" size={8} style={{ width: "100%" }}>
                    {linkedAccounts.map((account) => (
                      <Card key={account?.uid} size="small" bordered>
                        <Space align="start" size={12}>
                          {/* Provider logo field is intentionally spelled as returned by Bolt API. */}
                          {account?.financialInstitutuionLogoUrl ? (
                            <img
                              src={account.financialInstitutuionLogoUrl}
                              alt={account?.financialInstitutionName || "Bank logo"}
                              style={{ width: 24, height: 24, objectFit: "contain", borderRadius: 4 }}
                            />
                          ) : (
                            <WalletOutlined />
                          )}
                          <Space direction="vertical" size={0}>
                            <Text strong>{account?.financialInstitutionName || "Linked Bank"}</Text>
                            <Text type="secondary">Linked on: {account?.created ? formatdate(account.created) : "-"}</Text>
                          </Space>
                        </Space>
                      </Card>
                    ))}
                  </Space>
                )}
              </Space>
            </Card>

            <Modal
              title="Withdraw Funds"
              open={withdrawModalOpen}
              onOk={handleWithdraw}
              confirmLoading={withdrawSubmitting}
              okText="Confirm"
              onCancel={() => setWithdrawModalOpen(false)}
            >
              <p style={{ marginBottom: 10 }}>Select destination account</p>
              <Select
                style={{ width: "100%", marginBottom: 12 }}
                placeholder="Select linked account"
                value={selectedLinkedAccountUid}
                onChange={(value) => setSelectedLinkedAccountUid(value)}
                options={linkedAccounts.map((account) => ({
                  value: account?.uid,
                  label: `${account?.financialInstitutionName || "Linked Bank"} (${account?.uid || ""})`,
                }))}
              />

              <p style={{ marginBottom: 10 }}>Enter withdrawal amount</p>
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                max={Number(fundBalance?.balanceAvailable || 0)}
                precision={2}
                value={withdrawAmount}
                status={withdrawAmountError ? "error" : ""}
                onChange={(value) => {
                  const available = Number(fundBalance?.balanceAvailable || 0);
                  const amountNum = Number(value || 0);
                  setWithdrawAmount(value);
                  if (!amountNum || amountNum <= 0) {
                    setWithdrawAmountError("Please enter a valid amount.");
                  } else if (amountNum > available) {
                    setWithdrawAmountError("Amount exceeds available balance.");
                  } else {
                    setWithdrawAmountError(null);
                  }
                }}
                formatter={(value) => `$ ${value}`}
                parser={(value) => (value ? value.replace(/\$\s?|(,*)/g, "") : "")}
                placeholder="0.00"
              />
              {withdrawAmountError ? (
                <Text type="danger" style={{ fontSize: 12, marginTop: 6, display: "block" }}>
                  {withdrawAmountError}
                </Text>
              ) : null}
              <p style={{ marginTop: 10, marginBottom: 0 }}>
                Available: <strong>${Number(fundBalance?.balanceAvailable || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
              </p>
            </Modal>

            <Modal
              title="Link External Account"
              open={linkRequiredModalOpen}
              onCancel={() => setLinkRequiredModalOpen(false)}
              footer={[
                <Button key="cancel" onClick={() => setLinkRequiredModalOpen(false)}>
                  Cancel
                </Button>,
                <Button key="continue" type="primary" onClick={handleContinueToLinkAccount} loading={linkAccountSubmitting}>
                  Continue to Link Account
                </Button>,
              ]}
            >
              <p style={{ marginBottom: 12 }}>
                You need to link an external bank account before you can withdraw funds from your wallet.
              </p>
              <p style={{ marginBottom: 0 }}>
                Continuing will redirect you to the secure external account linking flow. After linking is complete, you will return to this wallet page to continue.
              </p>
            </Modal>
          </Card>
        ) : (
          <Card className="payments-transaction-card" bordered>
            <Title level={4} style={{ marginTop: 0 }}>Transaction History</Title>
            <Table
              rowKey={(row, idx) => row?.uid || `${idx}`}
              loading={walletDataLoading}
              columns={txColumns}
              dataSource={fundTransactions}
              pagination={false}
              scroll={{ x: 750 }}
            />
          </Card>
        )}
      </div>
    </div>
  );
}

export default Payments;












